import { Koi } from '@ksyos/koi';
import type { TypedReadSchemaItem } from '@common/formEngine/typedReadSchema.types';
import { YesOrNoOrStoppedOrUnknown } from '@common/enums';
import {
    alcoholDagenPerWeekSchema,
    alcoholGlazenPerDagSchema,
    smokingPacksADaySchema,
    smokingYearsSchema,
} from '@common/validation/schemas/numberSchemas';
import { getTextAreaStringSchema } from '@common/validation/utils/getTextAreaStringSchema';

export type IntoxicationCustomControlData = {
    alcoholStoppedSince: string | null;
    daysAWeek: number | null;
    drugUseStoppedSince: string | null;
    glassesADay: number | null;
    isAlcoholUser: YesOrNoOrStoppedOrUnknown | null;
    isDrugUser: YesOrNoOrStoppedOrUnknown | null;
    isDrugUserRemark: string | null;
    isSmoker: YesOrNoOrStoppedOrUnknown | null;
    isSmokerRemark: string | null;
    packsADaySmoked: number | null;
    smokingStoppedSince: string | null;
    yearsSmoked: number | null;
};

export const intoxicationValidation = {
    alcoholStoppedSince: getTextAreaStringSchema(true, 255),
    daysAWeek: alcoholDagenPerWeekSchema.allow(null),
    drugUseStoppedSince: getTextAreaStringSchema(true, 255),
    glassesADay: alcoholGlazenPerDagSchema.allow(null),
    isAlcoholUser: Koi.koi().enum(YesOrNoOrStoppedOrUnknown).allow(null),
    isDrugUser: Koi.koi().enum(YesOrNoOrStoppedOrUnknown).allow(null),
    isDrugUserRemark: getTextAreaStringSchema(true),
    isSmoker: Koi.koi().enum(YesOrNoOrStoppedOrUnknown).allow(null),
    isSmokerRemark: getTextAreaStringSchema(true),
    packsADaySmoked: smokingPacksADaySchema.allow(null),
    smokingStoppedSince: getTextAreaStringSchema(true, 255),
    yearsSmoked: smokingYearsSchema.allow(null),
};

export const intoxicationSchema = Koi.object(intoxicationValidation);

export type IntoxicationReadLabel =
    | 'Alcoholconsumptie'
    | 'Alcoholconsumptie dagen'
    | 'Alcoholconsumptie glazen'
    | 'Gestopt sinds'
    | 'Roken'
    | 'Drugsgebruik'
    | 'Jaar / pakjes per dag / pakjaren'
    | 'Toelichting';

export type IntoxicationReadData = IntoxicationCustomControlData & { years_packs: string[] };

export const intoxicationsReadItems: TypedReadSchemaItem<keyof IntoxicationReadData, IntoxicationReadLabel>[] = [
    { key: 'isAlcoholUser', label: 'Alcoholconsumptie' },
    { append: 'per week', key: 'daysAWeek', label: 'Alcoholconsumptie dagen', type: 'number' },
    { append: 'per dag', key: 'glassesADay', label: 'Alcoholconsumptie glazen', type: 'number' },
    { key: 'alcoholStoppedSince', label: 'Gestopt sinds' },
    { key: 'isSmoker', label: 'Roken' },
    { key: 'years_packs', label: 'Jaar / pakjes per dag / pakjaren' },
    { key: 'smokingStoppedSince', label: 'Gestopt sinds' },
    { key: 'isSmokerRemark', label: 'Toelichting' },
    { key: 'isDrugUser', label: 'Drugsgebruik' },
    { key: 'drugUseStoppedSince', label: 'Gestopt sinds' },
    { key: 'isDrugUserRemark', label: 'Toelichting' },
];
